import React, { useEffect, useRef } from 'react';
import our_mission from '../../assets/images/our_mission.jpg';
import our_vision from '../../assets/images/our_vision.png';
import { gsap } from "gsap";

const MissionVision = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subheadingRef = useRef(null);
  const missionCardRef = useRef(null);
  const visionCardRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate heading and subheading first
      gsap.fromTo(
        [headingRef.current, subheadingRef.current],
        {
          opacity: 0,
          y: 30,
          scale: 0.95,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.2,
        }
      );

      // Animate cards with more dynamic effect
      gsap.fromTo(
        [missionCardRef.current, visionCardRef.current],
        {
          opacity: 0,
          y: 40,
          scale: 0.9,
          rotationX: 10,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          rotationX: 0,
          duration: 1,
          ease: "power3.out",
          stagger: 0.25,
          delay: 0.3, // Start after heading/subheading
        }
      );
    }, sectionRef);

    return () => {
      ctx.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} className="w-full py-[60px] md:py-[80px] bg-[#F9F9FB]">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row justify-between md:mb-[40px] mb-[50px] md:mb-[50px] gap-4 md:gap-8">
          <h2 
            ref={headingRef} 
            className="w-full md:max-w-[418px] text-start md:text-left text-[28px] md:text-[44px] font-medium font-[LTWave] text-[#222222]"
          >
            Our Mission & Vision
          </h2>
          <p 
            ref={subheadingRef} 
            className="w-full md:max-w-[320px] text-start md:text-left text-base md:text-[18px] font-inter font-normal leading-[150%] tracking-normal text-[#4B4B4B]"
          >
            At Agastya Agro, our mission and vision shape every product, every partnership, and every promise to farmers.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-rows-1 md:grid-cols-2 gap-[30px] md:gap-[40px]">
          {/* Mission Card */}
          <div 
            ref={missionCardRef} 
            className="bg-white rounded-[20px] overflow-hidden shadow-md flex flex-col lg:flex-row h-full transition-transform duration-300 hover:scale-[1.02] hover:shadow-[0px_8px_45px_20px_#C0C0C050]"
          >
            {/* Mission Image */}
            <div className="w-full max-w-full lg:max-w-[312px] overflow-hidden rounded-[20px] opacity-100 group">
              <img
                src={our_mission}
                alt="Our Mission"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>

            {/* Mission Text */}
            <div className="w-full max-w-full lg:max-w-[272px] p-[25px] md:p-[30px] flex-grow">
              <div className="flex items-center mb-4">
                <div className="w-[12px] h-[12px] bg-blue-600 transform rotate-45 mr-3"></div>
                <h3 className="text-[22px] md:text-[26px] font-bold font-[LTWave] text-[#222222]">
                  Our Mission
                </h3>
              </div>
              <p className="text-[#4B4B4B] text-base font-regular font-inter leading-relaxed lg:mt-[76px] md:mt-[16px] mt-[16px] opacity-50">
                To deliver affordable, effective, and sustainable agricultural solutions 
                that promote farmer prosperity, boost agricultural productivity, and 
                support India's long-term food and economic security.
              </p>
            </div>
          </div>
          
          {/* Vision Card */}
          <div 
            ref={visionCardRef} 
            className="bg-white rounded-[20px] overflow-hidden shadow-md flex flex-col lg:flex-row h-full transition-transform duration-300 hover:scale-[1.02] hover:shadow-[0px_8px_45px_20px_#C0C0C050]"
          >
            {/* Vision Image */}
            <div className="w-full max-w-full h-[350px] md:h-full lg:max-w-[312px] overflow-hidden rounded-[20px] group">
              <img
                src={our_vision}
                alt="Our Vision"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
            
            {/* Vision Text */}
            <div className="w-full max-w-full lg:max-w-[272px] p-[25px] md:p-[30px] flex-grow">
              <div className="flex items-center mb-4">
                <div className="w-[12px] h-[12px] bg-blue-600 transform rotate-45 mr-3"></div>
                <h3 className="text-[22px] md:text-[26px] font-bold font-[LTWave] text-[#222222]">
                  Our Vision
                </h3>
              </div>
              <p className="text-[#4B4B4B] text-base font-regular font-inter leading-relaxed lg:mt-[76px] md:mt-[16px] mt-[16px] opacity-50">
                To lead the transformation of Indian agriculture into a sustainable, 
                high-yield sector by supporting farmers with innovative solutions for 
                soil and farm management through technology, education, research, 
                and community engagement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionVision;